export function renderAbout(): string {
  return /* html */`
<div class="page-wrapper about-page">

  <div class="page-hero">
    <span class="section-label">${L('Über Dresby', 'About Dresby')}</span>
    <h1 class="hero-title anim-fade-up">
      ${L('Software vom Rand<br/>', 'Software from the edge of<br/>')}
      <span class="gradient-text">${L('des Kontinents.', 'the continent.')}</span>
    </h1>
    <p class="hero-mission anim-fade-up" style="animation-delay:0.15s">
      ${L(
        'Wir sind ein kleines Software-Unternehmen mit Sitz in Dresden, Deutschland. Wir bauen KI-Werkzeuge, Web-Anwendungen, Open-Source-Systeme und experimentelle Schnittstellen — die Art von Technologie, bei der man das Gefühl hat, die Zukunft sei zu früh eingetroffen.',
        'We\'re a small software company based in Dresden, Germany. We build AI tools, web applications, open-source systems, and experimental interfaces — the kind of technology that makes you feel like the future arrived early.'
      )}
    </p>
  </div>

  <div class="page-section">

    <div class="about-grid">
      <div class="about-text">
        <h2 class="section-title" style="font-size:var(--text-3xl)">${L('Was Dresby ist', 'What Dresby is')}</h2>
        <div class="prose">
          <p>
            ${L(
              'Dresby begann als eine Sammlung von Obsessionen: ein Terminal-KI-Assistent, der tatsächlich verstand, wie Entwickler arbeiten, eine prozedurale Audio-Engine, die Synthese als Softwaredesign betrachtete, eine Schach-Engine, die zur Compiler-Erkundung wurde. Projekte, die sich weigerten, klein zu bleiben.',
              'Dresby started as a collection of obsessions: a terminal AI assistant that actually understood how developers work, a procedural audio engine that treated synthesis as software design, a chess engine that became a compiler exploration. Projects that refused to stay small.'
            )}
          </p>
          <p>
            ${L(
              'Irgendwann zeigten die Projekte auf etwas. Ein Unternehmen — nicht das Silicon-Valley-Modell, nicht die Enterprise-SaaS-Variante, sondern etwas Seltsameres. Software, gebaut von Menschen, denen es wichtig ist, wofür Software da ist.',
              'At some point the projects started pointing at something. A company — not the Silicon Valley kind, not the enterprise SaaS kind, but something stranger. Software built by people who care about what software is for.'
            )}
          </p>
          <p>
            ${L(
              'Dresden ist kein Zufall. Die Elbe hält dich ehrlich. Du baust Dinge, weil sie es wert sind, gebaut zu werden, nicht weil ein VC es sagt. Wir veröffentlichen, wenn die Arbeit fertig ist, nicht weil eine Metrik bewegt werden musste.',
              'Dresden isn\'t an accident. The Elbe river keeps you honest. You build things because they\'re worth building, not because a VC said so. We ship because the work is ready, not because a metric needed moving.'
            )}
          </p>
          <p>
            ${L(
              'Wir machen Apps, Tools, Spiele, Experimente und Open-Source-Bibliotheken. Manches ist für Entwickler. Manches ist für jeden, der einen Computer benutzt und möchte, dass diese Erfahrung besser wird. Alles davon ist echte Software, mit echter Sorgfalt gebaut, von einem kleinen Team, das tief darüber nachdenkt, was es baut.',
              'We make apps, tools, games, experiments, and open-source libraries. Some of it is for developers. Some of it is for anyone who uses a computer and wants that experience to be better. All of it is real software, built with real care, by a small team that thinks deeply about what it builds.'
            )}
          </p>
        </div>
      </div>

      <div class="about-facts">
        ${[
          { label: L('Standort', 'Location'), value: 'Dresden, Deutschland', sub: L('Im Herzen Sachsens, fernab vom Silicon Valley', 'In the heart of Saxony, far from Silicon Valley') },
          { label: L('Gegründet', 'Founded'), value: '2024' },
          { label: L('Team', 'Team'), value: L('Klein & wachsend', 'Small & growing'), sub: L('Remote-freundlich, Europa-basiert', 'Remote-friendly, Europe-based') },
          { label: L('Modell', 'Model'), value: L('Unabhängig', 'Independent'), sub: L('Kein VC-Funding. Nachhaltiges Wachstum.', 'No VC funding. Building sustainably.') },
        ].map(f => `
          <div class="fact-card card">
            <div class="fact-label">${f.label}</div>
            <div class="fact-value">${f.value}</div>
            ${f.sub ? `<div class="fact-sub">${f.sub}</div>` : ''}
          </div>
        `).join('')}
      </div>
    </div>

    <div class="divider"></div>

    <h2 class="section-title">${L('Woran wir glauben', 'What we believe')}</h2>
    <div class="beliefs-grid stagger">
      ${[
        { icon: '🔓', title: L('Offen per Default', 'Open by default'), body: L('Das meiste, was wir bauen, ist Open Source. GitHub ist der Ort, an dem unsere Arbeit öffentlich lebt — nicht als Marketing-Übung, sondern weil wir denken, dass Software einsehbar sein sollte.', 'Most of what we build is open source. GitHub is where our work lives publicly — not as a marketing exercise, but because we think software should be inspectable.') },
        { icon: '🏠', title: L('Local First', 'Local first'), body: L('Anwendungen, die deine Daten respektieren, offline funktionieren und deine Dateien nicht in der Cloud eines anderen als Geiseln halten. Dein Computer ist leistungsstark. Software sollte das nutzen.', 'Applications that respect your data, work offline, and don\'t hold your files hostage in someone else\'s cloud. Your computer is powerful. Software should use it.') },
        { icon: '🏰', title: L('Für die Ewigkeit gebaut', 'Built to last'), body: L('Wir bauen für Langlebigkeit, nicht nur für den Launch. Das bedeutet, langweilige Stack-Komponenten zu wählen wo angebracht, echte Dokumentation zu schreiben und Projekte nicht ohne Vorwarnung aufzugeben.', 'We build for longevity, not just for launch. That means choosing boring stack components where appropriate, writing real documentation, and not abandoning projects without warning.') },
        { icon: '⚡', title: L('Geschwindigkeit ist ein Feature', 'Speed is a feature'), body: L('Schnelle Software ist respektvolle Software. Wir nehmen Performance von Anfang an ernst, nicht als letzten Feinschliff vor der Veröffentlichung.', 'Fast software is respectful software. We take performance seriously from the beginning, not as a final polish step before release.') },
        { icon: '🔬', title: L('Offen experimentieren', 'Experiment openly'), body: L('Nicht jedes Projekt ist ein Produkt. Manche sind Erkundungen. Wir veröffentlichen Experimente, weil sie nützlich sein könnten und weil das Bauen Dinge lehrt, die schließlich in echte Produkte einfließen.', 'Not every project is a product. Some are explorations. We ship experiments because they might be useful and because building them teaches things that eventually feed into real products.') },
        { icon: '🐙', title: L('Keine Überwachung', 'No surveillance'), body: L('Wir verfolgen Benutzer nicht über das hinaus, was zur Bereitstellung des Dienstes notwendig ist. Wir verkaufen deine Daten nicht. Wir wollen deine Daten nicht.', 'We don\'t track users beyond what\'s necessary to provide the service. We won\'t sell your data. We don\'t want your data.') },
      ].map(b => `
        <div class="belief-card card">
          <div class="belief-icon">${b.icon}</div>
          <h3 class="belief-title">${b.title}</h3>
          <p class="belief-body">${b.body}</p>
        </div>
      `).join('')}
    </div>

    <div class="divider"></div>

    <!-- Tech stack -->
    <h2 class="section-title">${L('Wie wir bauen', 'How we build')}</h2>
    <p class="section-body" style="margin-bottom:var(--s8)">
      ${L(
        'Wir sind nicht dogmatisch bei Stack-Entscheidungen, aber wir haben starke Standards. Das findet sich in den meisten Dresby-Projekten:',
        'We\'re not dogmatic about stack choices, but we have strong defaults. Here\'s what you\'ll find across most Dresby projects:'
      )}
    </p>
    <div class="tech-grid">
      ${[
        { category: L('Sprachen', 'Languages'), items: ['Python', 'TypeScript', 'Rust', 'JavaScript'] },
        { category: L('Frontend', 'Frontend'), items: ['Vanilla TS', 'Three.js', 'Vite', 'Web APIs'] },
        { category: L('Backend', 'Backend'), items: ['FastAPI', 'Node.js', 'SQLite', 'Postgres'] },
        { category: L('Mobile', 'Mobile'), items: ['React Native', 'Swift', 'Kotlin'] },
        { category: L('KI / ML', 'AI / ML'), items: ['Anthropic Claude', 'OpenAI', 'HuggingFace', 'GGML'] },
        { category: L('Infrastruktur', 'Infrastructure'), items: ['Fly.io', 'Cloudflare', 'GitHub Actions'] },
      ].map(t => `
        <div class="tech-group card">
          <div class="tech-category">${t.category}</div>
          <div class="tech-items">
            ${t.items.map(i => `<span class="badge badge-dim">${i}</span>`).join('')}
          </div>
        </div>
      `).join('')}
    </div>

    <div class="divider"></div>

    <!-- CTA -->
    <div class="about-cta">
      <h2 class="section-title" style="font-size:var(--text-2xl)">${L('Baue etwas mit uns', 'Build something with us')}</h2>
      <p class="section-body">${L('Wir sind immer offen für Zusammenarbeit, die Sinn macht.', 'We\'re always open to collaborations that make sense.')}</p>
      <div class="hero-actions" style="margin-top:var(--s6)">
        <a href="#contact" class="btn btn-primary">${L('Dresby kontaktieren', 'Contact Dresby')}</a>
        <a href="#projects" class="btn btn-secondary">${L('GitHub-Arbeit ansehen', 'See GitHub Work')}</a>
      </div>
    </div>

  </div>
</div>
`;
}

import { i18n } from '../lib/i18n.ts';
function L(de: string, en: string): string { return i18n.current === 'de' ? de : en; }
